import React, { useState } from "react";
import { User, Mail, Phone, ArrowRight, Check, Star } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useEnquiry } from "../context/EnquiryContext";
import { IMAGES } from "../mock";

const HELP_OPTIONS = [
  "TMS treatment",
  "Accelerated TMS",
  "Esketamine",
  "Depression",
  "Anxiety",
  "OCD",
];

const TRUST = [
  "Caring team serving Sydney & Bundaberg since 2021",
  "3 out of 4 clients with depression respond to TMS",
  "Bulk-billed TMS for treatment-resistant depression",
  "Multi-disciplinary psychiatrists & registered nurses",
];

export default function EnquiryModal() {
  const enquiry = useEnquiry();
  const { toast } = useToast();
  const [form, setForm] = useState({
    location: "Sydney",
    name: "",
    email: "",
    phone: "",
    help: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!enquiry) return null;
  const { open, setOpen } = enquiry;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.help) {
      toast({
        title: "Please complete required fields",
        description: "Name, email, phone and how we can help are required.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "modal" }),
      });
      if (!res.ok) throw new Error("Request failed");
      setForm({ location: "Sydney", name: "", email: "", phone: "", help: "", message: "" });
      setOpen(false);
      toast({
        title: "Enquiry sent",
        description: "Thank you. The AwareHub team will get back to you shortly.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Your enquiry could not be sent. Please try again or call 1300 029 273.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-sand-50 border border-brand-200 px-4 py-3 text-brand-900 placeholder-brand-900/45 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden gap-0 border-brand-100 rounded-3xl"
        data-testid="enquiry-modal"
      >
        <div className="grid md:grid-cols-2">
          {/* Left — human touch image + trust */}
          <div className="relative hidden md:block">
            <img
              src={IMAGES.reception}
              alt="AwareHub team welcoming a client"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-950/90 via-brand-900/55 to-brand-800/25" />
            <div className="relative h-full flex flex-col justify-end p-7 text-white">
              <div className="flex items-center gap-1 text-brand-200 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4" fill="currentColor" />
                ))}
              </div>
              <h3 className="font-display text-2xl leading-tight">Why AwareHub?</h3>
              <p className="mt-2 text-sm text-white/80 leading-relaxed">
                Our experienced psychiatrists and nurses deliver advanced,
                evidence-based TMS and Esketamine treatments in a caring,
                supportive environment.
              </p>
              <ul className="mt-4 space-y-2.5">
                {TRUST.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-white/90">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-brand-300" strokeWidth={3} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — form */}
          <div className="p-6 sm:p-8">
            <DialogTitle className="font-display text-2xl font-normal leading-normal tracking-normal text-brand-900">Enquire Now</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-brand-900/60">
              Fill in your details and we'll get back to you
            </DialogDescription>

            <form onSubmit={handleSubmit} className="mt-5 space-y-3.5">
              <div className="flex items-center gap-6 text-brand-900/80 text-sm">
                {["Sydney", "Bundaberg"].map((loc) => (
                  <label key={loc} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      value={loc}
                      checked={form.location === loc}
                      onChange={onChange}
                      className="accent-brand-600 h-4 w-4"
                    />
                    {loc}
                  </label>
                ))}
              </div>

              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-brand-500" />
                <input name="name" value={form.name} onChange={onChange} placeholder="Your Name*" className={`${inputCls} pl-11`} data-testid="enquiry-modal-name" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-brand-500" />
                <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Your Email*" className={`${inputCls} pl-11`} data-testid="enquiry-modal-email" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-brand-500" />
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Your Phone*" className={`${inputCls} pl-11`} data-testid="enquiry-modal-phone" />
              </div>

              <Select value={form.help} onValueChange={(v) => set("help", v)}>
                <SelectTrigger className="w-full rounded-xl bg-sand-50 border border-brand-200 px-4 py-3 h-auto text-base text-brand-900 data-[placeholder]:text-brand-900/45 focus:ring-2 focus:ring-brand-200">
                  <SelectValue placeholder="How can we help you? *" />
                </SelectTrigger>
                <SelectContent>
                  {HELP_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <textarea name="message" value={form.message} onChange={onChange} rows={3} placeholder="Any additional information to assist you best?" className={`${inputCls} resize-none`} />

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cta-500 hover:bg-cta-600 disabled:opacity-60 text-white px-6 py-3.5 font-semibold tracking-wide uppercase transition-colors"
                data-testid="enquiry-modal-submit"
              >
                {submitting ? "Sending..." : "Submit"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
